import { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { Button } from "../APICommon";
import { ImageZoom } from "../ImageViewer";

export function BaseDetailModal({
  isOpen,
  onClose,
  title,
  content,
  image,
  icon: Icon,
  iconBgColor = "bg-blue-50",
  iconColor = "text-blue-600",
  sectionLabel = "Message",
  // ─── PATCHED: CC badge prop ───────────────────────────────────────────────
  isCc = false,
  // ─────────────────────────────────────────────────────────────────────────
}) {
  const [imageSrc, setImageSrc] = useState(null);

  useEffect(() => {
    const fetchImage = async () => {
      if (image) {
        setImageSrc("data:image/jpeg;base64," + image);
      } else {
        setImageSrc(null);
      }
    };
    fetchImage();
  }, [image, isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 text-gray-700 overflow-auto"
      >
        <div className="bg-gray-50 w-full md:w-[85%] lg:w-[50%] xl:w-[35%] rounded-xl shadow-2xl h-auto max-h-[90vh] flex flex-col relative border border-white/20">

          {/* Header */}
          <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-xl shadow-sm">
            <div className="flex items-center space-x-3 overflow-hidden">
              <div className={`p-1.5 ${iconBgColor} rounded-lg flex-shrink-0`}>
                <Icon className={iconColor} fontSize="small" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 tracking-tight truncate">
                {title}
              </h2>
              {/* ─── PATCHED: CC badge ────────────────────────────────────────── */}
              {isCc && (
                <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700 border border-indigo-200">
                  CC
                </span>
              )}
              {/* ──────────────────────────────────────────────────────────────── */}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all p-1.5 rounded-full border-none cursor-pointer flex-shrink-0"
            >
              <CloseIcon fontSize="medium" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 overflow-y-auto flex-grow flex flex-col space-y-6">
            <section className="flex-grow">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center">
                <span className="mr-2">{sectionLabel}</span>
                <div className="h-px flex-grow bg-gray-200"></div>
              </h3>
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                <div
                  className="text-gray-700 text-base leading-relaxed whitespace-pre-line"
                  dangerouslySetInnerHTML={{
                    __html: content ? content.replace(/\n/g, "<br />") : `No ${sectionLabel.toLowerCase()} content.`,
                  }}
                />
              </div>
            </section>

            {imageSrc && (
              <section className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center">
                  <span className="mr-2 flex items-center">
                    <AttachFileIcon fontSize="inherit" className="mr-1" />
                    Attachments
                  </span>
                  <div className="h-px flex-grow bg-gray-200"></div>
                </h3>
                <div className="flex justify-center bg-gray-100/50 border border-dashed border-gray-300 rounded-xl p-4 transition-all hover:bg-gray-100">
                  <ImageZoom
                    src={imageSrc}
                    alt="attachment"
                    imageClassName="max-h-[220px]"
                  />
                </div>
              </section>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end p-5 border-t border-gray-100 bg-white rounded-b-xl">
            <Button
              name={"Close"}
              className={"bg-gray-800 hover:bg-black text-white px-8 py-2.5 rounded-lg border-none cursor-pointer transition-all font-semibold text-sm shadow-sm active:scale-95"}
              onClick={onClose}
            />
          </div>
        </div>
      </div>
    </>
  );
}